import React, { useContext } from "react";
import { store } from "../components/Store.js";
import { Link, useLocation } from "react-router-dom";
import useObtenerSubsecciones from "../js/translations/useObtenerSubsecciones";
import { useId } from "../hooks/use-id.js";

/**
 * Componente para mostrar información y acciones relacionadas a un libro descargado offline, al dar click en la portada del libro es redirigido al visor para que renderice de manera offline el correspondiente libro.
 *
 * @component
 * @example
 * import { LibroOffline } from "./LibroOffline";
 *
 * function App() {
 *   return (
 *     <div>
 *       <LibroOffline idLibro="123" />
 *     </div>
 *   );
 * }
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.book - metadatos del libro.
 * @param {Function} props.onDelete - función que se ejecuta cuando se da click a eliminar libro.
 * @returns {JSX.Element} Componente LibroOffline.
 */
export const LibroOffline = ({ book, onDelete }) => {
	const { textosSubSecciones: descargados } = useObtenerSubsecciones("descargados");
	const globalState = useContext(store);
	const { state } = globalState;
	const isLogged = state.sesionActiva !== null && state.sesionActiva === "SI";
	const location = useLocation();
	const isPersonalAccount = book?.isPersonalAccount;
	const titleId = useId('title');
	const downloadedFrom =
		book?.downloadedFrom &&
		typeof book?.downloadedFrom === "string" &&
		book?.downloadedFrom?.trim().length > 0
			? book?.downloadedFrom
			: null;

	return (
		<>
			<article
				className="focusable-secondary row mx-2 my-4 pb-3 py-4 libro-descargado"
				role="region"
				aria-labelledby={titleId}
			>
				<div className="col-md-5 col-lg-3 d-flex resource-content pr-lg-5 pl-0 mb-1">
					<img
						role={"presentation"}
						className="img-fluid cover mx-auto mx-sm-0"
						src={book?.cover}
						alt={book?.title || 'cover'}
						aria-hidden="true"
					/>
				</div>
				<div className="col-md-7 col-lg-9 resource-content px-0 pl-lg-2 d-flex flex-column  ">
					<div>
						<h3 className="focusable-secondary mb-0 titulo" id={titleId}>
							{book?.title || "N/A"}
						</h3>
						<p  className="focusable-secondary mb-1 autor">
							{book?.author || "N/A"}
						</p>

						<p className="focusable-secondary tiempo-disponible">
							{descargados?.libro?.disponible || "Disponible hasta: "}
							<span className="primary-item"> {book?.dueDate}</span>
						</p>
						{isPersonalAccount && downloadedFrom && (
							<p className="blue-dark--color">
								<svg
									className="mr-1"
									width="18"
									height="20"
									viewBox="0 0 18 20"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									aria-hidden="true"
								>
									<path
										d="M14.3978 7.05882H12.4671V1.17647C12.4671 0.529412 11.9206 0 11.2528 0H6.39565C5.72779 0 5.18136 0.529412 5.18136 1.17647V7.05882H3.25065C2.16993 7.05882 1.6235 8.32941 2.3885 9.07059L7.96208 14.4706C8.43565 14.9294 9.20065 14.9294 9.67422 14.4706L15.2478 9.07059C16.0128 8.32941 15.4785 7.05882 14.3978 7.05882ZM0.324219 18.8235C0.324219 19.4706 0.870647 20 1.5385 20H16.1099C16.7778 20 17.3242 19.4706 17.3242 18.8235C17.3242 18.1765 16.7778 17.6471 16.1099 17.6471H1.5385C0.870647 17.6471 0.324219 18.1765 0.324219 18.8235Z"
										fill="currentColor"
									/>
								</svg>
								<span>{descargados?.libro?.descargado_desde || "Descargado desde"}</span>
								<span className="font-weight-bold"> {downloadedFrom}</span>
							</p>
						)}
					</div>

					<div>
						<button
							className="focusable-primary btn btn-eliminar mt-3"
							onClick={() => {
								onDelete && onDelete();
							}}
						>
							{descargados?.libro?.boton_eliminar || "Eliminar de mis descargas"}{" "}
							<i className="far fa-trash-alt"></i>
						</button>
					</div>
					<div className="d-flex d-sm-block">
						{(() => {
							let href = isLogged
								? `/visor/${book?.bookId}?returnUrl=${encodeURIComponent(
										location.pathname
								  )}`
								: "/login";
							return (
								<Link
									className="focusable-primary mx-auto mx-sm-0 btn btn-primary px-4 py-2 mt-3 mb-3 mb-sm-0"
									to={href}
								>
									{descargados?.libro?.boton_leer || "Leer"}
								</Link>
							);
						})()}
					</div>
				</div>
			</article>
		</>
	);
};
